import { isPlatformBrowser } from '@angular/common';
import { Injectable, Injector, PLATFORM_ID, inject, signal } from '@angular/core';
import {GeolocationService} from "@ng-web-apis/geolocation";
import { Observable, of, take } from "rxjs";
import {XploreItemDTO} from "../../shared/object/xplorer-be-parsed-classes";

export type UserLocationStatus = 'loading' | 'ready' | 'unavailable';

@Injectable({
  providedIn: 'root'
})
export class UserGeolocationService {

  private currentUserGeoPosition: GeolocationPosition | null = null;
  private geoLocationObservable: Observable<GeolocationPosition | null> = of(null);
  readonly userLocationStatus = signal<UserLocationStatus>('loading');
  readonly currentUserPosition = signal<GeolocationPosition | null>(null);
  private injector = inject(Injector);
  private platformId = inject(PLATFORM_ID);

  constructor() {
    if (!isPlatformBrowser(this.platformId)) {
      this.userLocationStatus.set('unavailable');
      return;
    }

    this.geoLocationObservable = this.injector.get(GeolocationService, of(null) as Observable<GeolocationPosition | null>);

    this.geoLocationObservable.pipe(take(10)).subscribe({
      next: (position) => {
        if (!position) {
          return;
        }

        console.debug('UserGeolocationService: ', position);
        this.currentUserGeoPosition = position;
        this.currentUserPosition.set(position);
        this.userLocationStatus.set('ready');
      },
      error: (error) => {
        console.debug('UserGeolocationService error: ', error);
        if (this.isPermissionDenied(error)) {
          this.userLocationStatus.set('unavailable');
          return;
        }

        // Keep "loading" for transient/unknown geolocation errors.
        this.userLocationStatus.set('loading');
      }
    });
  }

  public getDistanceXploreItemFromUserLocation(xploreItem: XploreItemDTO): string {
    return this.calculateDistance(xploreItem)?.toFixed(1);
  }

  private calculateDistance(xploreItem: XploreItemDTO): number {
    if (!xploreItem.geoPositionDTO) {
      return null;
    }

    if(!this.currentUserGeoPosition) {
      return null;
    }

    return this.calculateActualDistance(this.currentUserGeoPosition, xploreItem);
  }

  private calculateActualDistance(position: GeolocationPosition, xploreItem: XploreItemDTO) {
    const lat1 = position.coords.latitude;
    const lon1 = position.coords.longitude;
    const lat2 = xploreItem.geoPositionDTO.latitude;
    const lon2 = xploreItem.geoPositionDTO.longitude;

    const R = 6371e3; // metres
    const φ1 = lat1 * Math.PI / 180; // φ, λ in radians
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) *
      Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return ((R * c) / 1000); // in km
  }

  getCurrentUserLocation(): Observable<GeolocationPosition | null> {
    if(!this.currentUserGeoPosition) {
      return this.geoLocationObservable.pipe(take(1));
    }
    return of(this.currentUserGeoPosition);
  }

  getLocationStatus(): UserLocationStatus {
    return this.userLocationStatus();
  }

  private isPermissionDenied(error: unknown): boolean {
    if (!error) {
      return false;
    }

    const geolocationError = error as GeolocationPositionError;
    if (typeof geolocationError.code === 'number') {
      return geolocationError.code === geolocationError.PERMISSION_DENIED || geolocationError.code === 1;
    }

    const errorMessage = String((error as { message?: unknown })?.message || '').toLowerCase();
    return errorMessage.includes('permission') || errorMessage.includes('denied');
  }
}
